import { useState } from "react";
import { Bell, Check } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function NotificationAlarm() {
  const [kind, setKind] = useState("email");
  const [contact, setContact] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | ok | err
  const [errMsg, setErrMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!contact.trim()) return;
    setStatus("loading");
    setErrMsg("");
    try {
      await api.post("/alerts/subscribe", { contact: contact.trim(), kind });
      setStatus("ok");
      setContact("");
    } catch (err) {
      const m = err?.response?.data?.detail || "Could not subscribe. Try again.";
      setStatus("err");
      setErrMsg(m);
    }
  };

  return (
    <section
      className="bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200 rounded-xl p-5 md:p-6"
      aria-labelledby="notif-alarm-h2"
      data-testid="notification-alarm"
    >
      <div className="flex items-center gap-2 mb-1">
        <span className="inline-grid place-items-center w-8 h-8 rounded-md bg-orange-600 text-white">
          <Bell className="w-4 h-4" aria-hidden="true" />
        </span>
        <h2
          id="notif-alarm-h2"
          className="text-lg md:text-xl font-extrabold text-slate-900"
          style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}
        >
          Vacancy Notification Alarm
        </h2>
      </div>
      <p className="text-xs md:text-sm text-slate-700 mb-4 leading-relaxed">
        Get a heads-up the moment a new SSC / Railway / Banking / UPSC notification drops. Free. Unsubscribe anytime.
      </p>

      <form onSubmit={submit} className="grid gap-3" data-testid="notif-alarm-form">
        <div className="flex rounded-md overflow-hidden border border-slate-300 bg-white">
          <button
            type="button"
            data-testid="notif-kind-email"
            onClick={() => setKind("email")}
            className={`flex-1 min-h-11 px-3 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${
              kind === "email" ? "bg-orange-600 text-white" : "text-slate-600 hover:bg-slate-50"
            }`}
          >
            Email
          </button>
          <button
            type="button"
            data-testid="notif-kind-mobile"
            onClick={() => setKind("mobile")}
            className={`flex-1 min-h-11 px-3 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${
              kind === "mobile" ? "bg-orange-600 text-white" : "text-slate-600 hover:bg-slate-50"
            }`}
          >
            Mobile
          </button>
        </div>

        <input
          type={kind === "email" ? "email" : "tel"}
          inputMode={kind === "email" ? "email" : "numeric"}
          required
          value={contact}
          onChange={(e) => setContact(e.target.value)}
          placeholder={kind === "email" ? "you@example.com" : "10-digit mobile"}
          data-testid="notif-contact-input"
          className="w-full min-h-11 px-3 py-2 border border-slate-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
        />

        <Button
          type="submit"
          disabled={status === "loading" || !contact.trim()}
          data-testid="notif-submit"
          className="w-full bg-orange-600 hover:bg-orange-700 text-white min-h-11"
        >
          {status === "loading" ? "Subscribing…" : "Notify me of new vacancies"}
        </Button>

        {status === "ok" && (
          <div className="flex items-start gap-2 text-xs text-green-800 bg-green-50 border border-green-200 rounded-md p-3" role="status">
            <Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-green-700" />
            <span>You&apos;re on the list. We&apos;ll send you a heads-up at every major new Sarkari notification.</span>
          </div>
        )}
        {status === "err" && (
          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md p-3" role="alert">
            {errMsg}
          </div>
        )}
      </form>
      <p className="mt-3 text-[11px] text-slate-500 leading-relaxed">
        We send updates manually — no spam, no third-party sharing. By subscribing you agree to our{" "}
        <a href="/privacy-policy" className="underline hover:text-orange-700">privacy policy</a>.
      </p>
    </section>
  );
}
